// The owner's share sheet (V4-SH): make a private link to one version, copy it, turn links off, and see what viewers
// said. Opened from a version's share button (Versions.tsx) or by the Director's `share_sheet` directive, through
// share.ts openShare; <ShareHost/> (App.tsx) shows it wherever the user is.
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { director } from "../director";
import {
  byMoment,
  COMMENT_MAX,
  DEFAULT_EXPIRY,
  EXPIRY_CHOICES,
  fmtAt,
  linkMeta,
  linksFor,
  linkStatus,
  openShare,
  shareSource,
  shareTarget,
  subscribeShare,
  type OwnerComment,
  type ShareLink,
  type ShareList,
  type ShareTarget,
} from "../share";
import { bump, toast } from "../store";
import { Icon, Sheet, Spinner } from "./bits";
import { Fold } from "./Fold"; // v4 (V4-P2)

/** Mounted once (App.tsx): the share sheet for whatever openShare asked for. */
export function ShareHost() {
  const t = useSyncExternalStore(subscribeShare, shareTarget, () => null);
  if (!t) return null;
  return <ShareSheet key={`${t.projectId}/${t.version}`} target={t} onClose={() => openShare(null)} />;
}

/** The share icon: a box with an arrow out of it (bits.tsx has no share glyph). */
export function ShareIcon({ size = 18 }: { size?: number }) {
  return (
    <svg className="icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
      <path d="M12 15V4M8 8l4-4 4 4M6 11H5v9h14v-9h-1" />
    </svg>
  );
}

async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function ShareSheet({ target, onClose }: { target: ShareTarget; onClose: () => void }) {
  const [list, setList] = useState<ShareList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const [fresh, setFresh] = useState<string | null>(target.linkId ?? null);
  const load = useCallback(async () => {
    try {
      setList(await (await shareSource()).list(target.projectId, target.version));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [target.projectId, target.version]);
  useEffect(() => void load(), [load]);

  const links = list ? linksFor(list, target.version) : [];
  const live = links.filter((l) => l.state === "live");
  const old = links.filter((l) => l.state !== "live").slice(0, 5);
  const comments = list ? byMoment(list.comments.filter((c) => c.version === target.version)) : [];
  const showForm = making || (list !== null && !live.length);

  return (
    <Sheet title={`Share v${target.version}`} onClose={onClose} className="share-sheet">
      <div className="sheet-body share">
        <p className="share-lede">Anyone with a link can watch this version and leave comments at a moment in the video. They don't need an account, and the page never shows up in search. Their comments arrive as review notes.</p>
        {error && (
          <p className="form-error" role="alert">
            Couldn't load your links. {error}{" "}
            <button type="button" className="btn btn-quiet btn-small" onClick={() => void load()}>
              Try again
            </button>
          </p>
        )}
        {!list && !error && <Spinner label="Loading your links" />}

        {live.length > 0 && (
          <ul className="share-links" aria-label="Links that work">
            {live.map((l) => (
              <LinkRow key={l.id} l={l} fresh={l.id === fresh} onOff={(next) => setList((s) => (s ? { ...s, links: s.links.map((x) => (x.id === next.id ? next : x)) } : s))} />
            ))}
          </ul>
        )}

        {showForm ? (
          <NewLink
            target={target}
            canCancel={live.length > 0}
            onCancel={() => setMaking(false)}
            onMade={(l) => {
              setList((s) => (s ? { ...s, links: [l, ...s.links] } : { links: [l], comments: [] }));
              setFresh(l.id);
              setMaking(false);
            }}
          />
        ) : (
          list && (
            <button data-action="share.make" type="button" className="btn btn-quiet share-another" onClick={() => setMaking(true)}>
              <Icon name="plus" size={16} /> Make another link
            </button>
          )
        )}

        {comments.length > 0 && <Comments comments={comments} onDeleted={(id) => setList((s) => (s ? { ...s, comments: s.comments.filter((c) => c.id !== id) } : s))} />}

        {old.length > 0 && (
          <Fold id="share.old" title="Old links" count={old.length} summary="Ended or turned off" className="share-old">
            <ul className="share-links">
              {old.map((l) => (
                <li key={l.id} className="share-link is-dead">
                  <p className="share-url">{l.url.replace(/^https?:\/\//, "")}</p>
                  <p className="share-meta">
                    {linkStatus(l)} · {linkMeta(l)}
                  </p>
                </li>
              ))}
            </ul>
          </Fold>
        )}
      </div>
    </Sheet>
  );
}

function LinkRow({ l, fresh, onOff }: { l: ShareLink; fresh: boolean; onOff: (l: ShareLink) => void }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const canSend = typeof navigator !== "undefined" && typeof navigator.share === "function";
  return (
    <li className={`share-link${fresh ? " is-fresh" : ""}`}>
      <div className="share-url-row">
        <input className="share-url-input" readOnly value={l.url} aria-label="The link" onFocus={(e) => e.currentTarget.select()} />
      </div>
      <p className="share-meta">
        {linkStatus(l)} · {linkMeta(l)}
      </p>
      <div className="share-acts">
        <button
          data-action="share.copy"
          type="button"
          className="btn btn-primary"
          data-autofocus={fresh || undefined}
          onClick={async () => {
            if (await copy(l.url)) {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2200);
            } else toast("Couldn't copy it. Press and hold the link to copy it.", "bad");
          }}
        >
          <Icon name={copied ? "check" : "copy"} size={16} /> {copied ? "Copied" : "Copy link"}
        </button>
        {canSend && (
          <button data-action="share.copy" type="button" className="btn btn-quiet" onClick={() => void navigator.share({ url: l.url }).catch(() => undefined)}>
            Send…
          </button>
        )}
        <button
          data-action="share.revoke"
          type="button"
          className={`btn ${confirm ? "btn-danger" : "btn-quiet"}`}
          disabled={busy}
          onClick={async () => {
            if (!confirm) return setConfirm(true);
            setBusy(true);
            try {
              const next = await (await shareSource()).revoke(l.id);
              onOff(next);
              bump("project");
              director.context(`The user turned off a share link to version ${l.version} by tapping. It stopped working at once.`);
              toast("That link is off. It stopped working just now.", "good");
            } catch (e) {
              toast(`Couldn't turn it off. ${(e as Error).message}`, "bad");
              setBusy(false);
            }
          }}
          onBlur={() => setConfirm(false)}
        >
          {confirm ? "Turn it off for good" : "Turn off"}
        </button>
      </div>
    </li>
  );
}

function NewLink({ target, canCancel, onCancel, onMade }: { target: ShareTarget; canCancel: boolean; onCancel: () => void; onMade: (l: ShareLink) => void }) {
  const [days, setDays] = useState<number | null>(DEFAULT_EXPIRY);
  const [usePw, setUsePw] = useState(false);
  const [password, setPassword] = useState("");
  const [comments, setComments] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pwShort = usePw && password.trim().length < 4;
  return (
    <form
      className="share-form"
      onSubmit={async (e) => {
        e.preventDefault();
        if (pwShort) return setErr("Make the password at least 4 characters.");
        setBusy(true);
        setErr(null);
        try {
          const l = await (await shareSource()).create(target.projectId, target.version, { expiresInDays: days, password: usePw ? password.trim() : undefined, comments });
          onMade(l);
          bump("project");
          director.context(`The user made a share link to version ${target.version} by tapping (${linkStatus(l).toLowerCase()}${l.hasPassword ? ", with a password" : ""}${l.commentsOn ? "" : ", comments off"}). It's on screen with a Copy button.`);
        } catch (e2) {
          setErr((e2 as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <fieldset className="share-field">
        <legend className="field-label">Link works for</legend>
        <span className="seg share-seg" role="radiogroup" aria-label="Link works for">
          {EXPIRY_CHOICES.map((c) => (
            <button key={c.label} type="button" role="radio" aria-checked={days === c.days} className={days === c.days ? "is-on" : ""} onClick={() => setDays(c.days)}>
              {c.label}
            </button>
          ))}
        </span>
      </fieldset>
      <label className="share-check">
        <input type="checkbox" checked={comments} onChange={(e) => setComments(e.target.checked)} />
        <span>Let viewers leave comments</span>
      </label>
      <label className="share-check">
        <input type="checkbox" checked={usePw} onChange={(e) => setUsePw(e.target.checked)} />
        <span>Ask for a password</span>
      </label>
      {usePw && (
        <label className="field">
          <span className="field-label">Password</span>
          <input type="text" autoComplete="off" autoCapitalize="off" spellCheck={false} value={password} maxLength={200} onChange={(e) => setPassword(e.target.value)} placeholder="At least 4 characters" />
          <span className="field-hint">Send it to them separately from the link.</span>
        </label>
      )}
      {err && (
        <p className="form-error" role="alert">
          {err}
        </p>
      )}
      <div className="confirm-actions">
        {canCancel && (
          <button type="button" className="btn btn-quiet" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button data-action="share.make" type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? "Making it…" : "Make the link"}
        </button>
      </div>
    </form>
  );
}

function Comments({ comments, onDeleted }: { comments: OwnerComment[]; onDeleted: (id: string) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <section className="share-comments" aria-labelledby="share-c-h">
      <h3 id="share-c-h">What viewers said ({comments.length})</h3>
      <ul>
        {comments.map((c) => (
          <li key={c.id} className="share-comment">
            <span className="share-at">{fmtAt(c.atS)}</span>
            <div className="share-comment-text">
              <p className="share-who">{c.name}</p>
              <p className="share-said">{c.body.slice(0, COMMENT_MAX)}</p>
            </div>
            <button
              data-action="share.delete-comment"
              type="button"
              className="icon-btn"
              aria-label={`Delete ${c.name}'s comment`}
              title="Delete"
              disabled={busy === c.id}
              onClick={async () => {
                setBusy(c.id);
                try {
                  await (await shareSource()).deleteComment(c.id);
                  onDeleted(c.id);
                  bump("project");
                } catch (e) {
                  toast(`Couldn't delete it. ${(e as Error).message}`, "bad");
                } finally {
                  setBusy(null);
                }
              }}
            >
              <Icon name="trash" size={16} />
            </button>
          </li>
        ))}
      </ul>
      <p className="sheet-note">Each one is also a review note on this version. Deleting it here removes both.</p>
    </section>
  );
}
